import React, { useRef, useCallback, useState, useEffect, useContext, useMemo } from "react";
import styles from './CrmGrid.module.css';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'; // ✅ from the official plugin
import timezone from 'dayjs/plugin/timezone';
import { useNavigate } from "react-router-dom";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import SelectButtonRenderer from "./SelectButtonRenderer";
import { useTranslation } from "react-i18next";
import { LuFilter, LuFilterX, LuImport, LuDownload, LuMail, LuPhone } from "react-icons/lu";
import { MdOutlineCreateNewFolder } from "react-icons/md";
import { PiSelectionAll } from "react-icons/pi";
import { CgMoreVerticalO } from "react-icons/cg";
import { PiColumnsPlusLeft } from "react-icons/pi";
import { FaPlus, FaMinus } from "react-icons/fa";
import { FaLink, FaLinkSlash } from "react-icons/fa6";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { FaSpinner } from "react-icons/fa";
import LinkCellRenderer from "./LinkCellRenderer";
import PhoneEmailMenuRenderer from "./PhoneEmailMenuRenderer";
import { debounce } from '../../../../util/functions'
import AlertContext from '../../../../context/AlertModelContext/AlertModalContext';
import GridErrorBoundary from './GridErrorBoundary';

dayjs.extend(utc);
dayjs.extend(timezone);

const CrmGrid = ({
    rowData,
    columnDefs,
    multiSelectionEnable,
    onNewClicked,
    onFilterClicked,
    onClearFilters,
    supportManageColumns,
    supportMore,
    onRowClicked,
    onRemoveRowsClicked,
    pagination,
    onOrderChanged,
    className,
    onMinimizeToggleClicked,
    isGridMinimized = false,
    gridTitle,
    isSubGrid = false,
    onLinkClicked,
    onUnlinkClicked,
    onImportClicked,
    onExportClicked,
    onSendEmailClicked,
    onAddToDialListClicked,
    suppressOpenRecord,
    onSelectionChanged,
    isFiltersSelected = false,
    onSelectConv,
    OnAddToDialListClicked,
    // Server-side pagination props
    totalRecords = 0,
    onPaginationChanged,
    // Server-side sorting props
    onSortChanged,
    // Loading state
    loading = false,
    // Lead highlighting props
    selectedCrmModule
}) => {
    const gridRef = useRef(null);
    const [t] = useTranslation('crm');
    const navigate = useNavigate();
    const alertCtx = useContext(AlertContext)
    // 👇 Safer internal state to avoid mid-render updates
    const [internalData, setInternalData] = useState([]);
    const [headerSizeClass, setHeaderSizeClass] = useState('header-normal');

    // Pagination state for server-side pagination
    const currentPaginationRef = useRef({ page: 1, limit: 10 });
    const [customPageSize, setCustomPageSize] = useState(10);
    const [customCurrentPage, setCustomCurrentPage] = useState(1);
    const redrawTimeoutRef = useRef(null);
    const isGridRenderingRef = useRef(false);
    const gridReadyRef = useRef(false);

    const adjustHeaderFont = useCallback(() => {
        const gridWidth = gridRef.current?.gridBodyCtrl?.eBodyViewport?.clientWidth || 0;
        const totalColWidth = gridRef.current?.columnApi?.getAllDisplayedColumns()
            ?.reduce((sum, col) => sum + col.getActualWidth(), 0) || 0;
    
        if (totalColWidth > gridWidth * 2) {
            setHeaderSizeClass('header-xsmall');  
        } else if (totalColWidth > gridWidth * 1.5) {
            setHeaderSizeClass('header-small');
        } else if (totalColWidth > gridWidth) {
            setHeaderSizeClass('header-medium');
        } else {
            setHeaderSizeClass('header-normal');
        }
    }, []);
    
    useEffect(() => {
        if (!gridRef.current) return;
        adjustHeaderFont();
        window.addEventListener('resize', adjustHeaderFont);
        return () => window.removeEventListener('resize', adjustHeaderFont);
    }, [adjustHeaderFont]);
    
    // Also trigger after column resize / grid ready
    

    useEffect(() => {
        setInternalData(rowData || []);

        // Clear any pending redraw to avoid multiple concurrent redraws
        if (redrawTimeoutRef.current) {
            clearTimeout(redrawTimeoutRef.current);
        }

        // Redraw rows when data changes to apply new row classes
        // Only if grid is ready and not currently rendering
        if (gridRef.current && gridReadyRef.current && rowData?.length > 0) {
            redrawTimeoutRef.current = setTimeout(() => {
                // Double-check grid is not in rendering state
                if (isGridRenderingRef.current) {
                    // Skip this redraw, grid is busy
                    redrawTimeoutRef.current = null;
                    return;
                }

                try {
                    // Check if grid is still mounted and ready before redrawing
                    if (gridRef.current && gridRef.current.redrawRows) {
                        gridRef.current.redrawRows();
                    }
                } catch (error) {
                    // Silently suppress AG Grid render-during-render errors
                    if (!error.message?.includes('cannot get grid to draw rows') &&
                        !error.message?.includes('getLockOnRefresh')) {
                        console.error('Error redrawing grid rows:', error);
                    }
                }
                redrawTimeoutRef.current = null;
            }, 300);

            return () => {
                if (redrawTimeoutRef.current) {
                    clearTimeout(redrawTimeoutRef.current);
                    redrawTimeoutRef.current = null;
                }
            };
        }
    }, [rowData]);

    // For server-side pagination, we need to process the row data
    const processedRowData = useMemo(() => {
        if (!onPaginationChanged) {
            return internalData;
        }
        
        // Just return the actual data from the server for server-side pagination
        return internalData;
    }, [internalData, onPaginationChanged]);

    const resizeHandler = useCallback(
        debounce(() => {
            // Defer to avoid calling during render
            setTimeout(() => {
                gridRef.current?.sizeColumnsToFit?.();
            }, 0);
        }, 150),
        []
    );

    useEffect(() => {
        window.addEventListener('resize', resizeHandler);
        return () => window.removeEventListener('resize', resizeHandler);
    }, [resizeHandler]);

    // Dynamic pagination calculation functions - moved before onGridReady
    const calculateDynamicPageSize = useCallback((currentSize = 10) => {
        // Try to get the grid element via DOM query
        const gridElement = document.querySelector('.ag-theme-material');
        if (!gridElement) {
            return currentSize;
        }
        
        // Find the viewport container
        let bodyViewport = gridElement.querySelector('.ag-body-viewport');
        if (!bodyViewport) {
            bodyViewport = gridElement.querySelector('.ag-center-cols-viewport');
        }
        if (!bodyViewport) {
            // Fallback: use grid container height estimation
            const gridContainer = gridElement.closest('.grid-container');
            if (gridContainer) {
                const containerHeight = gridContainer.clientHeight;
                const headerHeight = 120; // approximate header height
                const paginationHeight = 60; // approximate pagination height
                const availableHeight = containerHeight - headerHeight - paginationHeight;
                const rowHeight = 40; // default row height
                const estimatedRows = Math.floor(availableHeight / rowHeight);
                const result = Math.max(10, Math.min(50, estimatedRows));
                return result;
            }
            return currentSize;
        }
        
        const exactViewportHeight = bodyViewport.clientHeight;
        let exactRowHeight = 40; // Default fallback
        
        // Try to measure an actual row, fallback to default if none exist
        const renderedRow = gridElement.querySelector('.ag-row');
        if (renderedRow) {
            exactRowHeight = renderedRow.getBoundingClientRect().height;
        }
        
        const maxRowsPerPage = Math.floor(exactViewportHeight / exactRowHeight);
        const result = Math.max(10, maxRowsPerPage);
        
        return result;
    }, []);

    const updateDynamicPageSize = useCallback(() => {
        if (!onPaginationChanged) return;

        const currentSize = currentPaginationRef.current.limit;
        const newSize = calculateDynamicPageSize(currentSize);

        if (Math.abs(newSize - currentSize) > 1) {
            setCustomPageSize(newSize);
            const newPage = Math.max(1, Math.min(customCurrentPage, Math.ceil(totalRecords / newSize)));
            if (newPage !== customCurrentPage) {
                setCustomCurrentPage(newPage);
            }
            currentPaginationRef.current = { page: newPage, limit: newSize };

            // Defer pagination change to avoid calling during grid render
            setTimeout(() => {
                onPaginationChanged({ page: newPage, limit: newSize });
            }, 0);
        }
    }, [calculateDynamicPageSize, customCurrentPage, totalRecords, onPaginationChanged]);

    const onGridReady = useCallback((params) => {
        gridRef.current = params.api;
        gridReadyRef.current = true;

        // Defer grid API calls to avoid calling during render
        setTimeout(() => {
            if (gridRef.current?.sizeColumnsToFit) {
                gridRef.current.sizeColumnsToFit();
            }
            adjustHeaderFont();
        }, 0);

        // Trigger dynamic page size calculation when grid is ready
        if (onPaginationChanged) {
            // Longer delay to ensure grid is fully rendered
            setTimeout(() => {
                updateDynamicPageSize();
            }, 500);
        }
    }, [adjustHeaderFont, onPaginationChanged, updateDynamicPageSize]);

    // Track when grid starts rendering to prevent API calls
    const onModelUpdated = useCallback(() => {
        isGridRenderingRef.current = true;
        // Reset rendering flag after render completes
        setTimeout(() => {
            isGridRenderingRef.current = false;
        }, 0);
    }, []);

    // Track when first data is rendered
    const onFirstDataRendered = useCallback(() => {
        isGridRenderingRef.current = false;
    }, []);

    const onPhoneEmailAction = useCallback((data) => {
        if (!data?.value || !data?.action) return alertCtx.showAlert(t('fail'), t('Failed to retrieve data from the grid.'), 'sm')
        switch (data?.action) {
            case 'copy':
                navigator.clipboard.writeText(data.value)
                return alertCtx.showAlert(t('success'), t('Copied to clipboard successfully.'), 'sm')
            case 'call':
                OnAddToDialListClicked?.(data.value)
                break;
            case 'whatsapp':
                onSelectConv?.(data.value, 'whatsapp')
                break;
            case 'sms':
                onSelectConv?.(data.value, 'sms')
                break;
            case 'mail':
                onSelectConv?.(data.value, 'mail')
                break
            default:
                alertCtx.showAlert(t('success'), t('Invalid action.'), 'sm')
        }
        navigate('/')
    }, [alertCtx, t])

    const defaultColDef = {
        sortable: !onOrderChanged && !!onSortChanged, // Enable sorting when onSortChanged is provided and onOrderChanged is not
        resizable: true,
        suppressMovable: !supportManageColumns,

        cellRendererSelector: p => {
            const v = p.value;
            return v && v._id && v.moduleType
                ? { component: 'linkCellRenderer' }   // שם תואם בדיוק
                : null;
        },

        cellClassRules: {
            'link-cell': params =>
                !!(params.value && typeof params.value === 'object' && params.value._id && params.value.moduleType),
        },
    };

    const hebrewLocaleText = {
        page: t('Page'),
        more: t('More'),
        to: t('To'),
        of: t('From'),
        next: t('Next'),
        last: t('Last'),
        first: t('First'),
        previous: t('Previous'),
    };

    const getRowClass = useCallback((params) => {
        const rowData = params.data;
        if (!rowData) {
            return '';
        }

        // Check if there's a CaseSta field in the row data
        const caseStaField = rowData.fields?.find(field => field.name === 'CaseSta' || field.name === 'activityFlow');

        if (caseStaField && caseStaField.value) {
            const color = caseStaField.value.toLowerCase().trim();

            // Map the color value to the corresponding CSS class
            switch (color) {
                case 'red':
                    return 'lead-row-red';
                case 'yellow':
                    return 'lead-row-yellow';
                case 'green':
                    return 'lead-row-green';
                case 'white':
                    return 'lead-row-white';
                case 'skyblue':
                    return 'lead-row-skyblue';
                default:
                    return '';
            }
        }

        return '';
    }, []);

    const gridOptions = {
        // domLayout: isSubGrid ? 'autoHeight' : undefined,
        domLayout: 'normal',
        suppressCellFocus: true,
        suppressDragLeaveHidesColumns: true,
        enableRtl: true,
        icons: {
            sortAscending: '<i class="fa fa-arrow-up" style="color: white;"></i>',
            sortDescending: '<i class="fa fa-arrow-down" style="color: white;"></i>',
        },
        localeText: hebrewLocaleText,
        // For server-side pagination, disable built-in pagination
        pagination: onPaginationChanged ? false : (onOrderChanged ? false : pagination),
        paginationPageSize: onPaginationChanged ? customPageSize : 10,
        paginationAutoPageSize: onPaginationChanged ? false : (onOrderChanged ? false : pagination),
        suppressRowClickSelection: true,
        rowHeight: 40,
        rowDragManaged: !!onOrderChanged,
        animateRows: true,
        getRowClass: getRowClass,
        // Suppress automatic row transforms to prevent render-during-render errors
        suppressRowTransform: false,
        // Suppress ResizeObserver errors that can interfere with rendering
        suppressBrowserResizeObserver: false,
        // Debounce row model updates to batch changes
        debounceVerticalScrollbar: true,
    };




    const colDef = columnDefs.map((col) => {
        const newCol = { ...col };

        if (['datetime', 'date'].includes(col.fieldType)) {
            newCol.valueFormatter = ({ value }) =>
                value ? dayjs.utc(value).tz('Asia/Jerusalem').format('HH:mm YYYY-MM-DD') : '';
        }

        if (col.fieldType === 'select') {
            newCol.valueFormatter = ({ value }) => {
                if (!value) return '';

                // Handle cascading dropdown objects
                if (typeof value === 'object' && value !== null && value.parent) {
                    const parts = [];

                    // Add children levels first (in reverse order for proper hierarchy)
                    if (value.children && value.children.length > 0) {
                        const sortedChildren = value.children.sort((a, b) => b.level - a.level);
                        parts.push(...sortedChildren.map(child => child.value || child.key));
                    }

                    // Add parent level
                    parts.push(value.parent);

                    return parts.filter(part => part).join('→');
                }

                // Handle simple string values
                return value.toString();
            };
        }

        if (['phone', 'email'].includes(col.fieldType)) {
            newCol.cellRenderer = 'moreMenuRenderer';
            newCol.cellRendererParams = {             // ← pass extras
                onAction: onPhoneEmailAction,
                fieldName: col.field,
            };
        }
        return newCol;
    });

    if (onOrderChanged) {
        colDef.unshift({
            headerName: t('Order'),
            field: 'drag',
            width: 80,
            rowDrag: true,
            pinned: 'right',
            suppressMovable: true,
        });
    } else if (multiSelectionEnable) {
        gridOptions.rowSelection = 'multiple';
        colDef.push({
            headerCheckboxSelection: true,
            checkboxSelection: true,
            checkboxSelectionSuppressDefault: false,//optional
            width: 65,
            pinned: 'right',
        });
        // if (suppressOpenRecord) {
        //     gridOptions.rowMultiSelectWithClick = true
        //     gridOptions.suppressRowClickSelection = false
        // }
        gridOptions.suppressRowClickSelection = true
    }

    const frameworkComponents = {
        selectButtonRenderer: SelectButtonRenderer,
        linkCellRenderer: LinkCellRenderer,
        moreMenuRenderer: PhoneEmailMenuRenderer,
    };

    const handleOnNewClicked = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onNewClicked) {
            onNewClicked();
        }
    }, [onNewClicked]);

    const handleOnFilterClicked = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onFilterClicked) {
            onFilterClicked();
        }
    }, [onFilterClicked]);

    const handleOnClearFiltersClicked = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onClearFilters) {
            onClearFilters();
        }
    }, [onClearFilters]);

    const onRemoveRowsClickHandler = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onRemoveRowsClicked) {
            onRemoveRowsClicked();
        }
    }, [onRemoveRowsClicked]);

    //onRemoveRowsClicked

    const onRowDragEnd = useCallback(() => {
        // Defer to avoid calling during render
        setTimeout(() => {
            if (!gridRef.current) return;

            const newOrder = [];
            gridRef.current.forEachNode((node) => {
                newOrder.push(node.data);
            });

            onOrderChanged?.(newOrder);
        }, 0);
    }, [onOrderChanged])

    const onLinkClickHandler = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onLinkClicked) {
            onLinkClicked()
        }
    }, [onLinkClicked])

    const onImportClickHandler = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onImportClicked) {
            onImportClicked()
        }
    }, [onImportClicked])

    const onExportClickHandler = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onExportClicked) {
            onExportClicked()
        }
    }, [onExportClicked])

    const onSendEmailClickHandler = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onSendEmailClicked) {
            onSendEmailClicked()
        }
    }, [onSendEmailClicked])

    const onAddToDialListClickHandler = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onAddToDialListClicked) {
            onAddToDialListClicked()
        }
    }, [onAddToDialListClicked])

    const onUnlinkHandler = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onUnlinkClicked) {
            onUnlinkClicked()
        }
    }, [onUnlinkClicked])

    const onSelectionChangedHandler = useCallback(() => {
        // Defer to avoid calling during render
        setTimeout(() => {
            const selected = gridRef.current?.getSelectedRows?.() || [];
            onSelectionChanged?.(selected); // pass up to parent
        }, 0);
    }, [onSelectionChanged]);

    const onSortChangedHandler = useCallback(() => {
        if (!onSortChanged || !gridRef.current) return;

        // Defer to avoid calling during render
        setTimeout(() => {
            if (!gridRef.current) return;

            // Get the current sort state from AG Grid
            const columnState = gridRef.current.getColumnState();
            const sortedColumn = columnState.find(col => col.sort !== null);

            if (sortedColumn) {
                // Map AG Grid sort direction to our API format ('asc' or 'desc')
                const order = sortedColumn.sort === 'asc' ? 'asc' : 'desc';
                const field = sortedColumn.colId;

                // Call the parent handler with the sort information
                onSortChanged({ field, order });
            }
        }, 0);
    }, [onSortChanged]);

    const handleCustomPageChange = useCallback((newPage) => {
        const totalPages = Math.max(1, Math.ceil(totalRecords / customPageSize));
        const safePage = Math.max(1, Math.min(newPage, totalPages));

        setCustomCurrentPage(safePage);
        currentPaginationRef.current = { page: safePage, limit: customPageSize };

        // Defer pagination change to avoid calling during grid render
        if (onPaginationChanged) {
            setTimeout(() => {
                onPaginationChanged({ page: safePage, limit: customPageSize });
            }, 0);
        }
    }, [customPageSize, totalRecords, onPaginationChanged]);

    // Initialize pagination ref with current state
    useEffect(() => {
        const safePage = Math.max(1, customCurrentPage);
        currentPaginationRef.current = { page: safePage, limit: customPageSize };
        
        // Ensure we're never on page 0
        if (customCurrentPage < 1) {
            setCustomCurrentPage(1);
        }
    }, [customCurrentPage, customPageSize]);
    
    // Initialize dynamic pagination on grid ready
    useEffect(() => {
        if (!onPaginationChanged || !gridRef.current) return;
        
        const timer = setTimeout(() => {
            updateDynamicPageSize();
        }, 100);
        
        return () => clearTimeout(timer);
    }, [gridRef.current, onPaginationChanged, updateDynamicPageSize]);
    
    // Update page size when data changes or window resizes
    useEffect(() => {
        if (!onPaginationChanged) return;
        
        const handleResize = debounce(() => {
            updateDynamicPageSize();
        }, 300);
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [onPaginationChanged, updateDynamicPageSize]);

    // const handleRowClicked = useCallback(
    //     (params) => {
    //         const el = params.event?.target;

    //         if (el && el.closest('.ag-link, .link-cell, .crm-clickable-cell')) return;

    //         onRowClicked?.(params);
    //     },
    //     [onRowClicked]
    // );

    const handleCellClicked = useCallback(
        (params) => {
            const colDef = params.colDef;
            const colId = params.column?.getColId?.();

            if (colId === 'ag-Grid-AutoColumn' || colDef?.checkboxSelection) {

                // If the user MISSED the actual <input>, AG Grid won’t toggle
                // anything, so we flip the row state ourselves.
                const clickedElement = params.event?.target;
                const clickedOnNativeCheckbox =
                    clickedElement?.closest('input[type="checkbox"]');

                if (!clickedOnNativeCheckbox) {
                    // Defer to avoid calling during render
                    setTimeout(() => {
                        const alreadySelected = params.node.isSelected();
                        params.node.setSelected(!alreadySelected);   // manual toggle
                    }, 0);
                }

                return;   // never open the record for this column
            }

            const el = params.event?.target;
            if (el && el.closest('.ag-link, .link-cell, .crm-clickable-cell')) {
                return;
            }

            onRowClicked?.(params);
        },
        [onRowClicked]
    );

    // Custom pagination panel component for server-side pagination
    const CustomPaginationPanel = ({ totalRecords, currentPage, pageSize, onPageChange }) => {
        const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
        const safePage = Math.max(1, Math.min(currentPage, totalPages));
        const startRecord = totalRecords > 0 ? (safePage - 1) * pageSize + 1 : 0;
        const endRecord = Math.min(safePage * pageSize, totalRecords);
        
        return (
            <div className={`${styles['custom-pagination']}`} dir="rtl">
                <div className={`${styles['pagination-info']}`}>
                    {totalRecords > 0 ? 
                        `${t('Showing')} ${startRecord}-${endRecord} ${t('of')} ${totalRecords} ${t('records')}` :
                        `${t('Showing')} 0 ${t('records')}`
                    }
                </div>
                <div className={`${styles['pagination-controls']}`} dir="ltr">
                    <button 
                        onClick={() => onPageChange(1)} 
                        disabled={safePage <= 1 || totalRecords === 0}
                        className={`${styles['pagination-btn']}`}
                    >
                        {t('first')}
                    </button>
                    <button 
                        onClick={() => onPageChange(safePage - 1)} 
                        disabled={safePage <= 1 || totalRecords === 0}
                        className={`${styles['pagination-btn']}`}
                    >
                        {t('previous')}
                    </button>
                    <span className={`${styles['page-info']}`}>
                        {t('Page')} {safePage} {t('of')} {totalPages}
                    </span>
                    <button 
                        onClick={() => onPageChange(safePage + 1)} 
                        disabled={safePage >= totalPages || totalRecords === 0}
                        className={`${styles['pagination-btn']}`}
                    >
                        {t('next')}
                    </button>
                    <button 
                        onClick={() => onPageChange(totalPages)} 
                        disabled={safePage >= totalPages || totalRecords === 0}
                        className={`${styles['pagination-btn']}`}
                    >
                        {t('last')}
                    </button>
                </div>
                <div className={`${styles['pagination-size-info']}`}>
                    {t('Page size')}: {pageSize}
                </div>
            </div>
        );
    };

    const gridElement = (
        <GridErrorBoundary>
            <AgGridReact
                components={frameworkComponents}
                gridOptions={gridOptions}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onModelUpdated={onModelUpdated}
                rowData={onPaginationChanged ? processedRowData : internalData}
                columnDefs={colDef}
                ref={gridRef}
                frameworkComponents={frameworkComponents}
                onRowDragEnd={onRowDragEnd}
                onSelectionChanged={onSelectionChangedHandler}
                onCellClicked={handleCellClicked}
                onSortChanged={onSortChangedHandler}
            />
        </GridErrorBoundary>
    );

    return (
        <div className={`grid-container ${styles['grid-container']} ${styles[headerSizeClass]} ag-theme-material ${!pagination && styles['grid-container--height-no-pagination']} ${loading ? styles['grid-container--loading'] : ''} ${className}`}>
            <div className={`${styles['grid-head']} ${isGridMinimized && styles['grid-head--closed']} ${onMinimizeToggleClicked && styles['grid-head--withminimize']}`}
                onClick={onMinimizeToggleClicked ? onMinimizeToggleClicked : () => { }}
            >
                <div className={`${styles['grid-head--right']}`}>
                    {onMinimizeToggleClicked &&
                        <>
                            {
                                isGridMinimized && <button className={`${styles['grid-btn']} ${styles['grid-btn--onlyicon']}`}>
                                    <FaPlus />
                                </button>
                            }
                            {
                                !isGridMinimized && <button className={`${styles['grid-btn']} ${styles['grid-btn--onlyicon']}`}>
                                    <FaMinus />
                                </button>
                            }
                        </>
                    }
                    {/* {multiSelectionEnable && (
                        <button className={`${styles['grid-btn']} ${styles['grid-btn--selectall']}`}>
                            <PiSelectionAll className={styles['select-all-btn']} /> {t('Select all')}
                        </button>
                    )} */}
                    {gridTitle && <h2 className={`${styles['grid-title']}`}>{gridTitle}</h2>}
                </div>
                <div className={`${styles['grid-head--left']}`}>
                    {supportMore && (
                        <button className={`${styles['grid-btn']} ${styles['grid-btn--more']}`}>
                            <CgMoreVerticalO />
                        </button>
                    )}
                    {supportManageColumns && (
                        <button className={`${styles['grid-btn']}`}>
                            <PiColumnsPlusLeft /> {t('Columns')}
                        </button>
                    )}

                    {onLinkClicked && !isGridMinimized && (
                        <button onClick={onLinkClickHandler} className={`${styles['grid-btn']}`}>
                            <FaLink /> {t('Link')}
                        </button>
                    )
                    }
                    {onUnlinkClicked && !isGridMinimized && (
                        <button onClick={onUnlinkHandler} className={`${styles['grid-btn']}`} style={{ "width": "140px" }}>
                            <FaLinkSlash /> {t('Unlink')}
                        </button>
                    )
                    }
                    {onRemoveRowsClicked && (
                        <button onClick={onRemoveRowsClickHandler} className={`${styles['grid-btn']}`}>
                            <MdOutlineDeleteOutline /> {t('Remove')}
                        </button>
                    )}
                    {onSendEmailClicked && (
                        <button onClick={onSendEmailClickHandler} title={t('Send Email')} className={`${styles['grid-btn']}`}>
                            <LuMail /> {t('Send')}
                        </button>
                    )}
                    {onAddToDialListClicked && (
                        <button onClick={onAddToDialListClickHandler} title={t('Add to Dial List')}className={`${styles['grid-btn']}`}>
                            <LuPhone /> {t('Add')}
                        </button>
                    )}
                    {onFilterClicked && !isGridMinimized && (//!isFiltersSelected &&
                        <button onClick={handleOnFilterClicked} className={`${styles['grid-btn']}`}>
                            <LuFilter /> {t('Filter')}
                        </button>
                    )}
                    {onClearFilters && !isGridMinimized && isFiltersSelected && (
                        <button onClick={handleOnClearFiltersClicked} className={`${styles['grid-btn']} ${styles['grid-btn--yellow']}`}>
                            <LuFilterX /> {t('Clear filters')}
                        </button>
                    )}
                    {onImportClicked && (
                        <button onClick={onImportClickHandler} className={`${styles['grid-btn']}`}>
                            <LuImport /> {t('Import')}
                        </button>
                    )
                    }
                    {onExportClicked && (
                        <button onClick={onExportClickHandler} className={`${styles['grid-btn']}`}>
                            <LuDownload /> {t('Export')}
                        </button>
                    )
                    }
                    {onNewClicked && !isGridMinimized && (
                        <button onClick={handleOnNewClicked} className={`${styles['grid-btn']}`}>
                            <MdOutlineCreateNewFolder /> {t('New')}
                        </button>
                    )}
                </div>
            </div>
            {!isGridMinimized && (
                <>
                    {isSubGrid
                        ? <div className={styles.gridViewport}>{gridElement}</div>
                        : gridElement
                    }
                    {onPaginationChanged && (
                        <CustomPaginationPanel 
                            totalRecords={totalRecords}
                            currentPage={customCurrentPage}
                            pageSize={customPageSize}
                            onPageChange={handleCustomPageChange}
                        />
                    )}
                </>
            )}
            {!pagination && !onPaginationChanged && <div className={`${styles['grid-footer']}`} />}
            {loading && (
                <div className={styles['loading-overlay']}>
                    <FaSpinner className={styles['loading-spinner']} />
                    <span className={styles['loading-text']}>{t('Loading...')}</span>
                </div>
            )}
        </div>
    );
};

export default CrmGrid;
